import { type ClientsListQueryParams } from '@entities/clients/types.ts';
import { groupsApi } from '@entities/common/groups/index.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import qs from 'query-string';
import { getRtkConfig } from '@shared/config';
import type { ArticlesPaginatedList } from '@shared/types/articles';
import type { Group, PaginatedList, SetClientToGroup } from '@shared/types/common';
import type { Consultation } from '@shared/types/consultations';
import { type ProgramsData } from '@shared/types/programs';
import type { Client } from '@shared/types/users';
import type { Note } from '@src/library/models/clients.ts';
import { clientApi } from './api';

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: getRtkConfig(''),
  tagTypes: [
    'clients',
    'client',
    'notes',
    'note',
    'individualPrograms',
    'consultations',
    'consultation',
    'groups',
    'group',
    'individualArticles',
  ],

  endpoints: (build) => ({
    getClients: build.query<PaginatedList<Client>, ClientsListQueryParams>({
      query: (params) => ({
        url: clientApi,
        params: params,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const key = {
          ...queryArgs,
          page: undefined,
        };
        return JSON.stringify(key);
      },
      merge: (loadedClients, newClients, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newClients;
        } else {
          const allClients = [...loadedClients.results, ...newClients.results];

          // Используем Map, чтобы сохранить только уникальные записи по id
          const uniqueClientsMap = new Map();
          for (const client of allClients) {
            uniqueClientsMap.set(client.id, client); // если id уже есть, перезапишет (что нормально)
          }

          return {
            ...loadedClients,
            next: newClients.next,
            results: Array.from(uniqueClientsMap.values()),
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['clients'],
    }),

    createClient: build.mutation<Client, FormData>({
      query: (body) => ({
        url: `/api/v1/users/client/`,
        body,
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        let listChanges;
        try {
          const resFromServer = await queryFulfilled;
          if (resFromServer.data) {
            const respData = resFromServer.data;
            const appState = getState();
            // берем arr всех кешированных аргументов у этой квери
            const cacheKeys = clientsApi.util.selectCachedArgsForQuery(appState, 'getClients');
            listChanges = dispatch(
              clientsApi.util.updateQueryData(
                'getClients',
                // берем агрументы последнего запроса
                cacheKeys[cacheKeys.length - 1],
                (draft) => {
                  draft.results.push(respData);
                }
              )
            );
          }
        } catch (e) {
          // console.log(e);
          listChanges && listChanges.undo();
        }
      },
    }),

    updateClient: build.mutation<Client, { id: number; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/api/v1/users/client/${id}/`,
        body,
        method: 'PATCH',
      }),
      async onQueryStarted(patchData, { dispatch, queryFulfilled, getState }) {
        // const queryParams =
        let listChanges;
        let clientChanges;
        try {
          const resFromServer = await queryFulfilled;
          if (resFromServer.data) {
            const respData = resFromServer.data;
            const appState = getState();
            // берем arr всех кешированных аргументов у этой квери
            const cacheKeys = clientsApi.util.selectCachedArgsForQuery(appState, 'getClients');
            listChanges = dispatch(
              clientsApi.util.updateQueryData(
                'getClients',
                // берем агрументы последнего запроса
                cacheKeys[cacheKeys.length - 1],
                (draft) => {
                  const lookedClientIndex = draft.results.findIndex((el) => el.id === respData.id);
                  draft.results[lookedClientIndex] = {
                    ...draft.results[lookedClientIndex],
                    ...respData,
                  };
                }
              )
            );

            clientChanges = dispatch(
              clientsApi.util.updateQueryData('getClientById', patchData.id, (draft) => {
                return {
                  ...draft,
                  ...respData,
                };
              })
            );
          }
        } catch (e) {
          // console.log(e);
          listChanges && listChanges.undo();
          clientChanges && clientChanges.undo();
        }
      },
    }),

    setClientColorTag: build.mutation<Client, { id: number; body: FormData }>({
      query: ({ id, body }) => ({
        url: ` /api/v1/users/client/${id}/set_color/`,
        body,
        method: 'POST',
      }),
      async onQueryStarted(postData, { dispatch, queryFulfilled, getState }) {
        // const queryParams =
        let postResult;

        const lookedColorTagId = postData.body.getAll('id');
        const appState = getState() as RootState;
        const colorTags = appState.common.colorTags.entities;
        const colorTagsValues = Object.values(colorTags);

        const lookedColorTag = colorTagsValues.find(
          (colorTag) => colorTag.id === Number(lookedColorTagId)
        );

        if (lookedColorTag) {
          postResult = dispatch(
            clientsApi.util.updateQueryData('getClientById', postData.id, (draft) => ({
              ...draft,
              color_tag: lookedColorTag.color,
            }))
          );
        }

        const resFromServer = await queryFulfilled;
        if (!resFromServer?.data) {
          postResult && postResult.undo();
        }
      },
      invalidatesTags: (_result, _error, args) => [{ type: 'client', id: args.id }],
    }),

    getNotes: build.query<
      PaginatedList<Note>,
      { client: number; page: number; search_text?: string }
    >({
      query: (params) => ({
        url: '/api/v1/notes/note/',
        params: params,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return qs.stringify(queryArgs, { arrayFormat: 'comma' });
      },
      merge: (loadedNotes, newNotes, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newNotes;
        } else {
          return {
            ...loadedNotes,
            next: newNotes.next,
            results: [...loadedNotes.results, ...newNotes.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['notes'],
    }),

    getNote: build.query<Note, number>({
      query: (id) => ({
        url: `/api/v1/notes/note/${id}/`,
        method: 'GET',
      }),
      providesTags: (result) => [{ type: 'note', id: result?.id }],
    }),

    createNote: build.mutation<Note, { client: number; content: string }>({
      query: (body) => ({
        url: `/api/v1/notes/note/`,
        body: {
          ...body,
        },
        method: 'POST',
      }),
      invalidatesTags: ['notes'],
    }),

    updateNote: build.mutation<Note, Pick<Note, 'content' | 'id'>>({
      query: ({ id, content }) => ({
        url: `/api/v1/notes/note/${id}/`,
        body: {
          content,
        },
        method: 'PATCH',
      }),
      async onQueryStarted(putData, { dispatch, queryFulfilled, getState }) {
        let currentNoteChanges;
        let noteListChanges;
        const appState = getState();
        const noteContent = putData.content;

        currentNoteChanges = dispatch(
          clientsApi.util.updateQueryData('getNote', putData.id, (draft) => {
            draft.content = noteContent;
          })
        );

        // берем arr всех кешированных аргументов у этой квери
        const cacheKeys = clientsApi.util.selectCachedArgsForQuery(appState, 'getNotes');
        noteListChanges = dispatch(
          clientsApi.util.updateQueryData(
            'getNotes',
            // берем агрументы последнего запроса
            cacheKeys[cacheKeys.length - 1],
            (draft) => {
              const lookedNoteIndex = draft.results.findIndex((el) => el.id === putData.id);
              if (lookedNoteIndex) {
                draft.results[lookedNoteIndex].content = noteContent;
              }
            }
          )
        );

        const resFromServer = await queryFulfilled;
        if (!resFromServer?.data) {
          currentNoteChanges && currentNoteChanges.undo();
          noteListChanges && noteListChanges.undo();
        }
      },
      invalidatesTags: (result) => [{ type: 'note', id: result?.id }],
    }),

    removeNote: build.mutation<undefined, number>({
      query: (id) => ({
        url: `/api/v1/notes/note/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['notes'],
    }),

    getIndividualPrograms: build.query<ProgramsData, { page: number; clientId: number }>({
      query: ({ page, clientId }) => ({
        url: `/api/v1/users/client/${clientId}/get_programs/`,
        params: { page },
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}_${qs.stringify(queryArgs, { arrayFormat: 'comma' })}`;
      },
      merge: (loadedPrograms, newPrograms, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newPrograms;
        } else {
          return {
            ...loadedPrograms,
            next: newPrograms.next,
            results: [...loadedPrograms.results, ...newPrograms.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['individualPrograms'],
    }),

    getIndividualArticles: build.query<ArticlesPaginatedList, { page: number; clientId: number }>({
      query: ({ page, clientId }) => ({
        url: `/api/v1/users/client/${clientId}/get_articles/`,
        params: { page },
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}_${qs.stringify(queryArgs, { arrayFormat: 'comma' })}`;
      },
      merge: (loadedArticles, newArticles, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newArticles;
        } else {
          return {
            ...loadedArticles,
            next: newArticles.next,
            results: [...loadedArticles.results, ...newArticles.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['individualArticles'],
    }),

    getConsultations: build.query<PaginatedList<Consultation>, { page: number; user: number }>({
      query: ({ page, user }) => ({
        url: `/api/v1/consultations/`,
        params: { page, user },
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return qs.stringify(queryArgs, { arrayFormat: 'comma' });
      },
      merge: (loadedConsultations, newConsultations, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newConsultations;
        } else {
          return {
            ...loadedConsultations,
            next: newConsultations.next,
            results: [...loadedConsultations.results, ...newConsultations.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['consultations'],
    }),

    getGroups: build.query<PaginatedList<Group>, { page: number; pagination?: boolean }>({
      query: (params) => ({
        url: `${groupsApi}/`,
        params,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return qs.stringify(queryArgs, { arrayFormat: 'comma' });
      },
      merge: (loadedGroups, newGroups, otherArgs) => {
        // console.log('merge');
        if (otherArgs?.arg?.page === 1) {
          return newGroups;
        } else {
          return {
            ...loadedGroups,
            next: newGroups.next,
            results: [...loadedGroups.results, ...newGroups.results],
          };
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['groups'],
    }),

    getGroup: build.query<Group, number>({
      query: (id) => ({
        url: `${groupsApi}${id}/`,
        method: 'GET',
      }),
      providesTags: (result) => [{ type: 'group', id: result?.id }],
    }),

    deleteGroup: build.mutation<undefined, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `${groupsApi}${groupId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['groups'],
    }),

    createGroup: build.mutation<Group, FormData>({
      query: (body) => ({
        url: `${groupsApi}/`,
        body,
        method: 'POST',
      }),
      invalidatesTags: (result) => ['groups', { type: 'group', id: result?.id }],
    }),

    updateGroup: build.mutation<Group, { id: number; body: FormData }>({
      query: ({ id, body }) => ({
        url: `${groupsApi}${id}/`,
        body,
        method: 'PATCH',
      }),
      invalidatesTags: (result) => ['groups', { type: 'group', id: result?.id }],
    }),

    setClientToGroup: build.mutation<undefined, SetClientToGroup>({
      query: (body) => ({
        url: `/api/v1/users/user/set_group/`,
        body,
        method: 'POST',
      }),
      invalidatesTags: (result, _, arg) => [
        { type: 'clients' },
        arg.all ? { type: 'client' } : undefined,
        { type: 'groups' },
        { type: 'group', id: arg.group },
        ...arg.users.map((id) => ({ type: 'client', id: String(id) }) as const),
      ],
    }),

    getClientById: build.query<Client, number>({
      query: (id) => clientApi + `${id}/`,
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        // const queryParams =
        let postResult;
        try {
          const resFromServer = await queryFulfilled;
          if (resFromServer.data) {
            const respData = resFromServer.data;
            const appState = getState();
            // берем arr всех кешированных аргументов у этой квери
            const cacheKeys = clientsApi.util.selectCachedArgsForQuery(appState, 'getClients');
            postResult = dispatch(
              clientsApi.util.updateQueryData(
                'getClients',
                // берем агрументы последнего запроса
                cacheKeys[cacheKeys.length - 1],
                (draft) => {
                  const lookedArticleIndex = draft.results.findIndex((el) => el.id === respData.id);
                  draft.results[lookedArticleIndex] = {
                    ...draft.results[lookedArticleIndex],
                    ...respData,
                  };
                }
              )
            );
          }
        } catch (e) {
          // console.log(e);
          postResult && postResult.undo();
        }
      },
      providesTags: (result) => [{ type: 'client', id: result?.id }],
    }),

    deleteClient: build.mutation<undefined, number>({
      query: (id) => ({
        url: `${clientApi}/${id}/`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // const queryParams =
        let postResult;
        try {
          const resFromServer = await queryFulfilled;

          if (resFromServer?.meta?.response?.status === 204) {
            const appState = getState();
            // берем arr всех кешированных аргументов у этой квери
            const cacheKeys = clientsApi.util.selectCachedArgsForQuery(appState, 'getClients');
            postResult = dispatch(
              clientsApi.util.updateQueryData(
                'getClients',
                // берем агрументы последнего запроса
                cacheKeys[cacheKeys.length - 1],
                (draft) => {
                  draft.results = draft.results.filter((client) => client.id !== id);
                }
              )
            );
          }
        } catch (e) {
          // console.log(e);
          postResult && postResult.undo();
        }
      },
    }),
  }),
});

export const clientsApiReducer = clientsApi.reducer;
export const clientsApiReducerPath = clientsApi.reducerPath;
export const clientsApiMiddleware = clientsApi.middleware;

export const {
  useGetClientsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useSetClientColorTagMutation,
  useGetClientByIdQuery,
  useGetNotesQuery,
  useGetNoteQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useRemoveNoteMutation,
  useGetIndividualProgramsQuery,
  useGetIndividualArticlesQuery,
  useGetConsultationsQuery,
  useGetGroupsQuery,
  useGetGroupQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useSetClientToGroupMutation,
  useDeleteClientMutation,
} = clientsApi;
